import React, { useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Link as MuiLink,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Search as SearchIcon,
  EmailOutlined as EmailIcon,
  Security as SecurityIcon,
  Assignment as DocumentIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';

const HowItWorks: React.FC = () => {
  const { t } = useTranslation();
  const { getWebPageData } = useStructuredData();
  const mainContentRef = useRef<HTMLElement>(null);
  const overviewRef = useRef<HTMLDivElement>(null);
  const signupRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  // Focus management for keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key >= '1' && event.key <= '5') {
        event.preventDefault();
        const refs = [overviewRef, signupRef, processRef, detailsRef, ctaRef];
        const index = parseInt(event.key) - 1;
        if (refs[index]?.current) {
          refs[index].current?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const pageTitle = t('howItWorks.title');
  const pageDescription = t('howItWorks.seo.description');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://alertalegal.co';
  const canonicalUrl = `${siteUrl}/how-it-works`;
  const structuredData = getWebPageData(pageTitle, pageDescription, '/how-it-works');


  return (
    <>
      <SEOErrorBoundary fallbackTitle="How It Works - AlertaLegal" fallbackDescription="Learn how AlertaLegal monitors legal publications and protects your identity in Costa Rica.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          canonical={canonicalUrl}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="xl">
        {/* Skip Links */}
        <Box
          sx={{
            position: 'absolute',
            left: '-9999px',
            '&:focus-within': {
              position: 'static',
              zIndex: 1300,
              bgcolor: 'primary.main',
              color: 'white',
              p: 2,
              textAlign: 'center',
            },
          }}
        >
          <MuiLink
            href="#main-content"
            sx={{
              color: 'white',
              textDecoration: 'underline',
              mr: 2,
              '&:focus': {
                outline: '2px solid white',
                outlineOffset: '2px',
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              mainContentRef.current?.focus();
            }}
          >
            {t('accessibility.skipToMain', 'Skip to main content')}
          </MuiLink>
          <MuiLink
            href="#overview"
            sx={{
              color: 'white',
              textDecoration: 'underline',
              mr: 2,
              '&:focus': {
                outline: '2px solid white',
                outlineOffset: '2px',
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              overviewRef.current?.focus();
            }}
          >
            {t('accessibility.skipToOverview', 'Skip to overview')}
          </MuiLink>
          <MuiLink
            href="#process"
            sx={{
              color: 'white',
              textDecoration: 'underline',
              '&:focus': {
                outline: '2px solid white',
                outlineOffset: '2px',
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              signupRef.current?.focus();
            }}
          >
            {t('accessibility.skipToProcess', 'Skip to process')}
          </MuiLink>
        </Box>
        
        <Box component="main" ref={mainContentRef} tabIndex={-1} sx={{ mt: 4, mb: 4 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 2,
              }}
            >
              {t('howItWorks.title')}
            </Typography>
            
            <Typography 
              variant="h6" 
              component="h2"
              sx={{ 
                color: 'text.secondary',
                fontSize: '1.1rem',
                maxWidth: '700px',
                mx: 'auto',
                mb: 4,
              }}
            >
              {t('howItWorks.subtitle')}
            </Typography>
          </Box>

          {/* Main Content Sections */}
          <Box sx={{ mb: 6, maxWidth: '1200px', mx: 'auto' }}>
            {/* First Row - Overview (Centered with max width) */}
            <Grid container spacing={4} sx={{ mb: 4, justifyContent: 'center' }}>
              <Grid size={{ xs: 12, lg: 8 }}>
                <Card 
                  ref={overviewRef}
                  tabIndex={0}
                  role="region"
                  aria-labelledby="overview-heading"
                  aria-describedby="overview-description"
                  sx={{ 
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&:focus': {
                      outline: '3px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                      <SearchIcon sx={{ fontSize: 50, color: 'primary.main', mr: { xs: 0, md: 2 }, mb: { xs: 2, md: 0 } }} />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" component="h3" id="overview-heading" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                          {t('howItWorks.sections.whatIs.title')}
                        </Typography>
                        <Typography variant="h6" component="p" id="overview-description" sx={{ color: 'text.secondary', lineHeight: 1.6, maxWidth: '800px' }}>
                          {t('howItWorks.sections.whatIs.description')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Second Row - Process Steps */}
            <Grid container spacing={4} sx={{ mb: 4, justifyContent: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card 
                  ref={signupRef}
                  tabIndex={0}
                  role="region"
                  aria-labelledby="signup-heading"
                  aria-describedby="signup-description"
                  sx={{ 
                    height: '100%',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&:focus': {
                      outline: '3px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <DocumentIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2, mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" component="h3" id="signup-heading" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                          {t('howItWorks.sections.howToSignUp.title')}
                        </Typography>
                        <Typography variant="body1" id="signup-description" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                          {t('howItWorks.sections.howToSignUp.description')}
                        </Typography>
                      </Box>
                    </Box>
                    <List sx={{ flex: 1 }} role="list" aria-label={t('howItWorks.sections.howToSignUp.title')}>
                      {[
                        t('howItWorks.sections.howToSignUp.steps.step1'),
                        t('howItWorks.sections.howToSignUp.steps.step2'),
                        t('howItWorks.sections.howToSignUp.steps.step3'),
                      ].map((step, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }} role="listitem">
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Chip 
                              label={index + 1} 
                              size="small" 
                              sx={{ 
                                bgcolor: 'primary.main', 
                                color: 'white',
                                fontWeight: 600,
                                width: 24,
                                height: 24,
                                fontSize: '0.75rem',
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={step}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: 'text.secondary', lineHeight: 1.5 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card 
                  ref={processRef}
                  tabIndex={0}
                  role="region"
                  aria-labelledby="process-heading"
                  aria-describedby="process-description"
                  sx={{ 
                    height: '100%',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&:focus': {
                      outline: '3px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2, mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" component="h3" id="process-heading" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                          {t('howItWorks.sections.whatHappens.title')}
                        </Typography>
                        <Typography variant="body1" id="process-description" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                          {t('howItWorks.sections.whatHappens.description')}
                        </Typography>
                      </Box>
                    </Box>
                    <List sx={{ flex: 1 }} role="list" aria-label={t('howItWorks.sections.whatHappens.title')}>
                      {[
                        t('howItWorks.sections.whatHappens.steps.step1'),
                        t('howItWorks.sections.whatHappens.steps.step2'),
                        t('howItWorks.sections.whatHappens.steps.step3'),
                      ].map((step, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }} role="listitem">
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Chip 
                              label={index + 1} 
                              size="small" 
                              sx={{ 
                                bgcolor: 'secondary.main', 
                                color: 'white',
                                fontWeight: 600,
                                width: 24,
                                height: 24,
                                fontSize: '0.75rem',
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={step}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: 'text.secondary', lineHeight: 1.5 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Third Row - Details */}
            <Grid container spacing={4} sx={{ mb: 4, justifyContent: 'center' }} ref={detailsRef}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card 
                  tabIndex={0}
                  role="region"
                  aria-labelledby="publications-heading"
                  aria-describedby="publications-description"
                  sx={{ 
                    height: '100%',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&:focus': {
                      outline: '3px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexDirection: 'column', textAlign: 'center' }}>
                      <PublicIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h5" component="h3" id="publications-heading" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        {t('howItWorks.sections.whatPublications.title')}
                      </Typography>
                      <Typography variant="body1" id="publications-description" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                        {t('howItWorks.sections.whatPublications.description')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                          {t('howItWorks.sections.whatPublications.current.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'success.dark' }}>
                          {t('howItWorks.sections.whatPublications.current.description')}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                          {t('howItWorks.sections.whatPublications.future.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'info.dark' }}>
                          {t('howItWorks.sections.whatPublications.future.description')}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'center' }}>
                        {t('howItWorks.sections.whatPublications.capability')}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card 
                  tabIndex={0}
                  role="region"
                  aria-labelledby="privacy-heading"
                  aria-describedby="privacy-description"
                  sx={{ 
                    height: '100%',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&:focus': {
                      outline: '3px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexDirection: 'column', textAlign: 'center' }}>
                      <SecurityIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h5" component="h3" id="privacy-heading" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        {t('howItWorks.sections.dataPrivacy.title')}
                      </Typography>
                      <Typography variant="body1" id="privacy-description" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                        {t('howItWorks.sections.dataPrivacy.description')}
                      </Typography>
                    </Box>
                    
                    <List sx={{ flex: 1 }} role="list" aria-label={t('howItWorks.sections.dataPrivacy.title')}>
                      {[
                        t('howItWorks.sections.dataPrivacy.points.secure'),
                        t('howItWorks.sections.dataPrivacy.points.control'),
                        t('howItWorks.sections.dataPrivacy.points.informational'),
                      ].map((point, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }} role="listitem">
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={point}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: 'text.secondary', lineHeight: 1.5 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card 
                  tabIndex={0}
                  role="region"
                  aria-labelledby="why-matters-heading"
                  aria-describedby="why-matters-description"
                  sx={{ 
                    height: '100%',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&:focus': {
                      outline: '3px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexDirection: 'column', textAlign: 'center' }}>
                      <InfoIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h5" component="h3" id="why-matters-heading" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        {t('howItWorks.sections.whyMatters.title')}
                      </Typography>
                      <Typography variant="body1" id="why-matters-description" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                        {t('howItWorks.sections.whyMatters.description')}
                      </Typography>
                    </Box>
                    
                    <List sx={{ flex: 1 }} role="list" aria-label={t('howItWorks.sections.whyMatters.title')}>
                      {[
                        t('howItWorks.sections.whyMatters.reasons.buried'),
                        t('howItWorks.sections.whyMatters.reasons.consequences'),
                        t('howItWorks.sections.whyMatters.reasons.solution'),
                      ].map((reason, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }} role="listitem">
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={reason}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: 'text.secondary', lineHeight: 1.5 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Call to Action Section */}
          <Card 
            ref={ctaRef}
            tabIndex={0}
            role="region"
            aria-labelledby="cta-heading"
            aria-describedby="cta-description"
            sx={{ 
              mt: 6, 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:focus': {
                outline: '3px solid white',
                outlineOffset: '2px',
              },
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" component="h3" id="cta-heading" sx={{ fontWeight: 600, mb: 2 }}>
                {t('howItWorks.cta.title')}
              </Typography>
              <Typography variant="body1" id="cta-description" sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
                {t('howItWorks.cta.description')}
              </Typography>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="large"
                aria-describedby="cta-description"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                  '&:focus': {
                    outline: '3px solid',
                    outlineColor: 'secondary.main',
                    outlineOffset: '2px',
                  },
                }}
              >
                {t('howItWorks.cta.button')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default HowItWorks;